import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'qs';
import { UserInfo } from '@/account/Interfaces';
import { useServiceToken } from '@/auth/JwtAuthenticator/JwtAuthenticator';
import { useSession } from '@/auth/Session';
import { useAppConfigContext } from '@/context/AppConfig';
import { tuxedoRequest } from '@/utils/index';
import { HomeData, TuxedoUserProfile, UserProfile } from './interfaces';
import { Address } from '../types/serialized';

const queryKey = 'tuxedo';
const maxTuxedoPollAttempt = 10;

export class Tuxedo {
  public static async getUserProfile(userCodeParam: string, token: string) {
    const userCode = qs.stringify({
      u_c: userCodeParam,
    });

    return await tuxedoRequest(`/tux/v1/user_profiles?${userCode}`, {
      method: 'GET',
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }

  public static async fetchHomeAdditionsApi(
    // When fetching credit hub actions we don't need the permalink
    activePermalink: string | null,
    userCodeParam: string,
    token: string
  ) {
    const queryString = qs.stringify({ addr_hash: activePermalink });
    const userCode = qs.stringify({
      u_c: userCodeParam,
    });

    return await tuxedoRequest(`/tux/v1/uds?${queryString}&${userCode}`, {
      method: 'GET',
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }

  public static async submitHomeDataSet(
    homeData: HomeData,
    activePermalink: string,
    userCodeParam: string,
    token: string
  ) {
    const userCode = qs.stringify({
      u_c: userCodeParam,
    });
    const queryString = qs.stringify({ addr_hash: activePermalink });

    return await tuxedoRequest(`/tux/v1/uds?${queryString}&${userCode}`, {
      method: 'POST',
      body: JSON.stringify(homeData),
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }

  public static async postUserProfile(
    userProfile: TuxedoUserProfile,
    token: string
  ) {
    return await tuxedoRequest('/tux/v1/user_profiles', {
      method: 'POST',
      body: JSON.stringify(userProfile),
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }

  public static async getPrepMortyiFrame(customerId: string, token: string) {
    const queryParam = `?${new URLSearchParams({
      customer_id: customerId,
    }).toString()}`;
    return await tuxedoRequest(`/tux/v1/prep_morty_iframe${queryParam}`, {
      method: 'GET',
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }

  public static async getRecommendations(token: string) {
    return await Tuxedo.poll({
      method: 'GET',
      url: '/tux/v1/recommendations',
      token,
    });
  }

  public static async poll({
    method,
    url,
    token,
  }: {
    method: string;
    url: string;
    token: string;
  }) {
    const request = () =>
      tuxedoRequest(url, {
        method,
        headers: {
          'content-type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

    let result = await request();

    let pollAttempt = 0;

    while (result?.status === 202 && pollAttempt < maxTuxedoPollAttempt) {
      await Tuxedo.wait();
      result = await request();

      pollAttempt += 1;
    }

    if (result?.status === 202) {
      return Promise.reject(new Error('Tuxedo max polling reached'));
    }

    return result;
  }

  public static wait(ms = 3000) {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  }
}

export function useTuxedo() {
  const { logError } = useAppConfigContext();
  const queryClient = useQueryClient();

  const { data: tokenData } = useServiceToken('tuxedo');
  const { session } = useSession();

  const userCode = session?.account?.account?.user_info?.user_code || '';

  const userProfile = useQuery({
    queryKey: [queryKey],
    queryFn: () => Tuxedo.getUserProfile(userCode, tokenData?.token),
    onError: (error) => {
      logError('Error fetching user profile', error as Error);
    },
    enabled: !!tokenData?.token && !!userCode,
  });

  const homeAdditions = (activePermalink: string | null) =>
    useQuery({
      queryKey: ['tuxedo_homeAdditions', activePermalink],
      queryFn: () =>
        Tuxedo.fetchHomeAdditionsApi(
          activePermalink,
          userCode,
          tokenData?.token
        ),

      onError: (error) => {
        logError('Error fetching home additions', error as Error);
      },
      enabled: !!tokenData?.token && !!userCode,
      // Currently the request of home additions credit hub actions could fail
      // if the user doesn't have a user_profile, so to avoid retrying we disable the retry
      retry: activePermalink === null ? false : 3,
    });

  const homeDataSet = useMutation({
    mutationFn: (params: { homeData: HomeData; activePermalink: string }) => {
      return Tuxedo.submitHomeDataSet(
        params.homeData,
        params.activePermalink,
        userCode,
        tokenData?.token
      );
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKey],
      });
    },
    onError: (error) => {
      logError('Error submitting home data set', error as Error);
    },
  });

  const createUserProfile = useMutation({
    mutationFn: ({
      userProfile,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onSuccess,
    }: {
      userProfile: TuxedoUserProfile;
      onSuccess?: () => void;
    }) => {
      return Tuxedo.postUserProfile(userProfile, tokenData?.token);
    },

    onSuccess: (data, { onSuccess }) => {
      queryClient.invalidateQueries({
        queryKey: [queryKey],
      });
      onSuccess && onSuccess();
    },
    onError: (error) => {
      logError('Error posting user profile', error as Error);
    },
  });

  const recommendations = (
    initialRecordSearch: Record<string, string>,
    partner: string
  ) =>
    useQuery({
      queryKey: ['tuxedo_recommendations', initialRecordSearch, partner],
      queryFn: () => Tuxedo.getRecommendations(tokenData?.token),
      onError: (error) => {
        logError('Error fetching recommendations', error as Error);
      },
      enabled: false,
    });

  const prepMortyiFrame = useMutation({
    mutationFn: (customerId: string) =>
      Tuxedo.getPrepMortyiFrame(customerId, tokenData?.token),
    onError: (error) => {
      logError('Error fetching prepMortyiFrame', error as Error);
    },
  });

  const setupUserProfile = (
    userData: UserInfo | undefined,
    address: Address | undefined
  ) => {
    const userProfile: UserProfile = {
      email: userData?.email || '',
      phone: userData?.phone_number || '',
      first_name: userData?.first_name || '',
      last_name: userData?.last_name || '',
      user_code: userData?.user_code || '',
      record_search: {
        address: address?.parts?.street || '',
        city: address?.parts?.city || '',
        state: address?.parts?.state || '',
        zip_code: address?.parts?.zip || '',
      },
    };
    return userProfile;
  };

  const setUpRecordSearch = (address: Address | undefined) => {
    return {
      address: address?.parts?.street || '',
      city: address?.parts?.city || '',
      state: address?.parts?.state || '',
      zip_code: address?.parts?.zip || '',
    };
  };

  return {
    userProfile,
    createUserProfile,
    setupUserProfile,
    homeAdditions,
    homeDataSet,
    recommendations,
    setUpRecordSearch,
    prepMortyiFrame,
  };
}
